"use client";

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import { IconPauseFill, IconPlayFill, IconX } from "@/components/icons";

/**
 * Owns the single <audio> element for 30-second track previews, and the
 * mini-player docked above the tab bar.
 *
 * This lives above AppShell rather than inside the tracklist for one reason:
 * an <audio> element unmounted by a route change stops playing. Keeping it
 * here is what lets a preview keep going while the visitor carries on browsing
 * the collection.
 *
 * See docs/superpowers/specs/2026-08-23-song-previews-design.md.
 */

export type PreviewTrack = {
  id: string;
  title: string;
  artist: string;
  url: string;
  coverUrl: string | null;
  /** The item this track belongs to, used to re-resolve a stale URL. */
  itemId: string;
};

type AudioPreviewValue = {
  currentTrackId: string | null;
  isPlaying: boolean;
  /** 0-1 through the clip, for the mini-player's progress bar. */
  progress: number;
  /** Track ids whose URL failed to play this session. */
  unavailable: ReadonlySet<string>;
  /** Plays a track. `queue` is the rest of the tracklist, for auto-advance. */
  play: (track: PreviewTrack, queue: PreviewTrack[]) => void;
  toggle: (track: PreviewTrack, queue: PreviewTrack[]) => void;
  stop: () => void;
};

const AudioPreviewContext = createContext<AudioPreviewValue | null>(null);

export function useAudioPreview() {
  const ctx = useContext(AudioPreviewContext);
  if (!ctx) throw new Error("useAudioPreview must be used inside AudioPreviewProvider");
  return ctx;
}

export function AudioPreviewProvider({ children }: { children: React.ReactNode }) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const queueRef = useRef<PreviewTrack[]>([]);
  // Whether the current track has already been retried with a freshly
  // resolved URL. Providers rotate preview URLs, so a cached one can start
  // 403ing months after it was stored — but one retry only, or a genuinely
  // dead track would loop against the API forever.
  const retriedRef = useRef<string | null>(null);

  const [current, setCurrent] = useState<PreviewTrack | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [unavailable, setUnavailable] = useState<Set<string>>(new Set());

  const markUnavailable = useCallback((trackId: string) => {
    setUnavailable((prev) => {
      const next = new Set(prev);
      next.add(trackId);
      return next;
    });
  }, []);

  const startTrack = useCallback((track: PreviewTrack, queue: PreviewTrack[]) => {
    queueRef.current = queue;
    retriedRef.current = null;
    setCurrent(track);
    setProgress(0);
    const audio = audioRef.current;
    if (!audio) return;
    audio.src = track.url;
    // A rejected play() is the autoplay policy or a dead URL; the `error`
    // handler below covers the URL case, so there is nothing to do but stop
    // pretending we are playing.
    void audio.play().catch(() => setIsPlaying(false));
  }, []);

  const play = useCallback(
    (track: PreviewTrack, queue: PreviewTrack[]) => startTrack(track, queue),
    [startTrack],
  );

  const toggle = useCallback(
    (track: PreviewTrack, queue: PreviewTrack[]) => {
      const audio = audioRef.current;
      if (!audio) return;
      if (current?.id === track.id) {
        if (audio.paused) void audio.play().catch(() => setIsPlaying(false));
        else audio.pause();
        return;
      }
      startTrack(track, queue);
    },
    [current, startTrack],
  );

  /** Pause/resume whatever is loaded — the mini-player's own button, which has
   *  no tracklist context to hand to `toggle`. */
  const toggleCurrent = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || !audio.src) return;
    if (audio.paused) void audio.play().catch(() => setIsPlaying(false));
    else audio.pause();
  }, []);

  const stop = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.removeAttribute("src");
      audio.load();
    }
    queueRef.current = [];
    setCurrent(null);
    setIsPlaying(false);
    setProgress(0);
  }, []);

  const advance = useCallback(() => {
    const queue = queueRef.current;
    const index = queue.findIndex((t) => t.id === current?.id);
    const next = queue.slice(index + 1).find((t) => !unavailable.has(t.id));
    if (next) startTrack(next, queue);
    else stop();
  }, [current, unavailable, startTrack, stop]);

  /**
   * A cached URL that no longer resolves gets one re-resolution attempt
   * against the provider before the track is written off for the session.
   * Only the owner can hit that endpoint, so for a visitor this fails fast
   * and falls through to marking the track unavailable.
   */
  const handleError = useCallback(async () => {
    const track = current;
    if (!track) return;

    if (retriedRef.current === track.id) {
      markUnavailable(track.id);
      advance();
      return;
    }
    retriedRef.current = track.id;

    try {
      const res = await fetch("/api/lookup/previews", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ itemId: track.itemId }),
      });
      if (!res.ok) throw new Error("refresh failed");
      const data = (await res.json()) as { previews: Record<string, string> };
      const fresh = data.previews[track.id];
      if (!fresh || fresh === track.url) throw new Error("no fresher url");

      const audio = audioRef.current;
      if (!audio) return;
      setCurrent({ ...track, url: fresh });
      audio.src = fresh;
      void audio.play().catch(() => setIsPlaying(false));
    } catch {
      markUnavailable(track.id);
      advance();
    }
  }, [current, markUnavailable, advance]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onTime = () => {
      // Clip length varies slightly between providers (~30s), so read it off
      // the element instead of assuming 30.
      const total = Number.isFinite(audio.duration) && audio.duration > 0 ? audio.duration : 30;
      setProgress(Math.min(1, audio.currentTime / total));
    };
    const onEnded = () => advance();
    const onError = () => void handleError();

    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);
    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("ended", onEnded);
    audio.addEventListener("error", onError);
    return () => {
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("ended", onEnded);
      audio.removeEventListener("error", onError);
    };
  }, [advance, handleError]);

  const value = useMemo<AudioPreviewValue>(
    () => ({
      currentTrackId: current?.id ?? null,
      isPlaying,
      progress,
      unavailable,
      play,
      toggle,
      stop,
    }),
    [current, isPlaying, progress, unavailable, play, toggle, stop],
  );

  return (
    <AudioPreviewContext.Provider value={value}>
      {children}
      {/* No caption track: these are 30-second clips streamed from a
          third-party CDN, which publishes no captions for them. `preload="none"`
          keeps every page that mounts this provider from opening a connection
          before anyone has asked to hear anything. */}
      <audio ref={audioRef} preload="none" />
      <AudioPreviewBar
        track={current}
        isPlaying={isPlaying}
        progress={progress}
        onToggle={toggleCurrent}
        onStop={stop}
      />
    </AudioPreviewContext.Provider>
  );
}

function AudioPreviewBar({
  track,
  isPlaying,
  progress,
  onToggle,
  onStop,
}: {
  track: PreviewTrack | null;
  isPlaying: boolean;
  progress: number;
  onToggle: () => void;
  onStop: () => void;
}) {
  // Reserves scroll room for the bar so the last tracklist row never sits
  // underneath it. AppShell's <main> folds this variable into its bottom
  // padding; a `position: fixed` bar cannot do it through layout.
  useEffect(() => {
    const root = document.documentElement;
    if (track) root.style.setProperty("--preview-bar-space", "4.75rem");
    else root.style.removeProperty("--preview-bar-space");
    return () => {
      root.style.removeProperty("--preview-bar-space");
    };
  }, [track]);

  if (!track) return null;

  return (
    <div
      // `--tab-bar-space` is published by MobileTabBar only while it is
      // actually rendered. It is absent for a signed-out visitor on a public
      // profile, where there is no tab bar to clear, and the fallback drops
      // the player to the bottom edge instead of leaving it floating in the
      // middle of the tracklist.
      className="fixed inset-x-0 bottom-[calc(var(--tab-bar-space,0px)+0.5rem)] z-40 px-3 md:bottom-3"
      // Same reasoning as the tab bar it sits above: iOS Safari drifts a fixed,
      // backdrop-filtered layer during momentum scroll unless it is promoted.
      style={{ transform: "translateZ(0)", willChange: "transform" }}
    >
      <div className="glass-floating glass-specular mx-auto flex max-w-sm items-center gap-3 rounded-ios-lg p-2.5">
        <PreviewArtwork coverUrl={track.coverUrl} />

        <div className="min-w-0 flex-1">
          <p className="truncate text-[13px] font-semibold text-ios-label">{track.title}</p>
          <p className="truncate text-[11px] text-ios-label-secondary">{track.artist}</p>
          <div className="mt-1.5 h-[3px] overflow-hidden rounded-full bg-ios-fill">
            <div
              className="h-full rounded-full bg-ios-accent transition-[width] duration-200 ease-linear"
              style={{ width: `${Math.round(progress * 100)}%` }}
            />
          </div>
        </div>

        <PreviewBarButtons isPlaying={isPlaying} onToggle={onToggle} onStop={onStop} />
      </div>
    </div>
  );
}

function PreviewArtwork({ coverUrl }: { coverUrl: string | null }) {
  if (!coverUrl) {
    return <div className="h-10 w-10 shrink-0 rounded-ios-sm bg-ios-fill" />;
  }
  // The URL is an already-presigned R2 link resolved server-side; next/image
  // would only re-proxy it.
  // eslint-disable-next-line @next/next/no-img-element
  return <img src={coverUrl} alt="" className="h-10 w-10 shrink-0 rounded-ios-sm object-cover" />;
}

function PreviewBarButtons({
  isPlaying,
  onToggle,
  onStop,
}: {
  isPlaying: boolean;
  onToggle: () => void;
  onStop: () => void;
}) {
  return (
    <div className="flex shrink-0 items-center gap-1">
      <button
        type="button"
        onClick={onToggle}
        aria-label={isPlaying ? "Pause preview" : "Resume preview"}
        className="press-shrink flex h-9 w-9 items-center justify-center rounded-full text-ios-label"
      >
        {isPlaying ? <IconPauseFill className="h-4 w-4" /> : <IconPlayFill className="h-4 w-4" />}
      </button>
      <button
        type="button"
        onClick={onStop}
        aria-label="Close preview"
        className="press-shrink flex h-7 w-7 items-center justify-center rounded-full bg-ios-fill text-ios-label-secondary"
      >
        <IconX className="h-3 w-3" />
      </button>
    </div>
  );
}
